import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/Button";
import { ProductCard } from "@/features/product/ProductCard";
import { QuantitySelector } from "../../QuantitySelector";
import {
  useVariant,
  useQuantity,
  useSelectedItemContext,
} from "../../SelectedItemContext";
import { DrawerPageProps } from "../types";
import { Page, PortalDrawer, EditUpcomingOrderDrawer } from "..";
import { filterMatches } from "@/components/SearchInput";
import { useAccountContext } from "../../AccountContext";
import { DroppsVariant, productSelect, variantSelect } from "@/store";
import { droppsProductToProductCardProduct } from "../../helpers";
import { ToggleButton } from "@/components/ToggleButton";

import { PdpProvider } from "@/features/pdp/PdpContext";
import { Money } from "@/helpers/Money";

interface EditProductPageProps extends DrawerPageProps {}

const EditProductPage = (props: EditProductPageProps) => {
  const { notify } = props;
  const order = useSelectedItemContext().boxItem;
  const { products } = useAccountContext();
  const quantityUpdator = useQuantity();
  // Ensure type safety before accessing the `percentage` property
  const discount = order.product.sellingPlans[0].discount;
  const [newQty, setNewQty] = useState(order.rechargeItem.quantity);
  const [size, setSize] = useState(order.variant.package_quantity + " count");

  // Function to calculate discounted price
  const getDiscountedPrice = (price: Money, discount: any) => {
    if (discount.kind === "percentage") {
      const discountedCents = price.asCents() * (1 - discount.percentage / 100);
      return Money.from(discountedCents, "¢");
    } else if (discount.kind === "fixed") {
      const discountedCents = price.asCents() - discount.offset.asCents();
      return Money.from(discountedCents, "¢");
    } else if (discount.kind === "price") {
      return discount.price;
    } else {
      return price;
    }
  };

  const [variantPrice, setVariantPrice] = useState(() => {
    return getDiscountedPrice(order.rechargeItem.price, discount);
  });

  const variantUpdator = useVariant();
  const [newVariant, setNewVariant] = useState(
    order.rechargeItem.shopify_variant_id
  );

  useEffect(() => {
    const selectedVariant = order.product.variants.find(
      (v) => v.id === newVariant
    );
    if (selectedVariant) {
      const discountedPrice = getDiscountedPrice(
        selectedVariant.price,
        order.product.sellingPlans[0].discount
      );
      setVariantPrice(discountedPrice);
      setSize(selectedVariant.package_quantity + " count");
    }
  }, [newVariant, order.product.variants, order.product.sellingPlans]);

  const [openedDrawer, setOpenedDrawer] = useState<Page | undefined>(undefined);

  const product = {
    ...droppsProductToProductCardProduct(order),
    price: undefined,
  };
  const showScents: boolean = useMemo(() => {
    const scent = productSelect.scent(order.product);
    const size = variantSelect.sizeFormat(order.variant);
    const name = productSelect.name(order.product);
    return !!(
      scent &&
      scent !== size &&
      filterMatches(products, (product) => product.title, name)
    );
  }, [order, products]);

  const handleVariantUpdate = () => {
    variantUpdator.mutate(
      { variantId: newVariant, quantity: newQty },
      {
        onSuccess: () => {
          notify({
            title: "Size Updated",
            message: `Your order now contains ${newQty} x ${size}`,
          });
        },
        onError: () => {
          notify({
            title: "Error",
            message: `An error occurred. Please try again.`,
            severity: "error",
          });
        },
      }
    );
  };

  const handleVariantChange = (value: number | null) => {
    if (value !== null) {
      const variant = order.product.variants.find((v) => v.id === value);
      if (variant) {
        setSize(variant.package_quantity + " count");
        setNewVariant(value);
      }
    }
  };

  const handleQuantityUpdate = () => {
    variantUpdator.mutate(
      { variantId: newVariant, quantity: newQty },
      {
        onSuccess: () => {
          notify({
            title: "Quantity Updated",
            message: `Your order now contains ${newQty} x ${size}`,
          });
        },
        onError: () => {
          notify({
            title: "Error",
            message: `An error occurred. Please try again.`,
            severity: "error",
          });
        },
      }
    );
  };

  const handleQuantityChange = (value: number | null) => {
    if (value) setNewQty(value);
  };

  const VariantButton = ({
    variant,
    className,
  }: {
    variant: DroppsVariant;
    className?: string;
  }) => {
    // Apply 'bg-primary-50' only if this variant is the one currently selected
    const isSelected = variant.id === newVariant;

    return (
      <div
        data-id="variants-button"
        key={variant.id}
        className={`flex-1 ${isSelected ? "bg-primary-50" : ""} ${className}`}
      >
        {variant?.quantity_discount !== undefined &&
          variant.quantity_discount > 0 && (
            <p
              className={`buy-more-save-more-label ${
                isSelected ? "bg-primary-800" : ""
              }`}
            >
              Save {variant.quantity_discount}%
            </p>
          )}

        <ToggleButton
          className="w-full"
          selected={isSelected}
          size="md"
          onClick={() => handleVariantChange(variant.id)}
          label={
            <span className="text-base">
              {variantSelect.sizeFormat(variant)}
            </span>
          }
          name="variant-button"
        />
      </div>
    );
  };

  return (
    <>
      <div className="w-full">
        <h3 className="subtitle mb-4 text-base font-semibold">Change Size</h3>

        <div
          role="radiogroup"
          className="flex gap-2"
          aria-label={`Select product variant`}
        >
          <PdpProvider product={order.product} scents={[]}>
            {order.product.variants.map((x) => (
              <VariantButton
                key={variantSelect.reactKey(x)}
                variant={x}
                className={""}
              />
            ))}
          </PdpProvider>
        </div>

        <Button
          className="mt-4 w-full"
          onClick={handleVariantUpdate}
          disabled={order.rechargeItem.shopify_variant_id === newVariant}
        >
          Update Size
        </Button>
      </div>
      <div className="w-full">
        <QuantitySelector
          label="Change Quantity"
          value={newQty}
          size={size}
          price={variantPrice}
          onChange={handleQuantityChange}
        />
        <Button
          className="mt-4 w-full"
          onClick={handleQuantityUpdate}
          disabled={order.rechargeItem.quantity === newQty}
        >
          Update Quantity
        </Button>
      </div>
      <div>
        <h3 className="subtitle mb-4 text-base font-semibold">
          Change Product
        </h3>
        <ProductCard
          productCardValue={product}
          compact={true}
          hoverable={false}
          bordered
        />
      </div>
      <div className="w-full space-y-4">
        {showScents && (
          <Button
            className="w-full"
            onClick={() =>
              setOpenedDrawer({
                kind: "swap-item-scent",
                isSubscription: order.rechargeItem.status !== "ONETIME",
                scentFilter: productSelect.name(order.product),
                cadence: order.rechargeItem.cadence,
              })
            }
          >
            Swap Scent
          </Button>
        )}
        <Button
          variant={showScents ? "outline" : "solid"}
          className="w-full"
          onClick={() =>
            setOpenedDrawer({
              kind: "swap-item",
              isSubscription: order.rechargeItem.status !== "ONETIME",
              cadence: order.rechargeItem.cadence,
            })
          }
        >
          Swap Product
        </Button>
      </div>
      <PortalDrawer
        open={openedDrawer !== undefined}
        title={"Edit Item"}
        contents={(nav) =>
          openedDrawer !== undefined ? (
            <EditUpcomingOrderDrawer page={openedDrawer} {...nav} />
          ) : null
        }
        {...props}
        onBack={() => setOpenedDrawer(undefined)}
        transparentBackdrop={true}
      />
    </>
  );
};

export { EditProductPage };
